import calendlyLogo from '@/assets/images/calendly_logo.png';
import elevenlabsLogo from '@/assets/images/elevenlabs_logo.svg';
import ghlLogo from '@/assets/images/ghl_logo.svg';
import oncehubLogo from '@/assets/images/oncehub_logo.png';
import useIntegrationsStore from '@/stores/IntegrationsStore';
import { AlertCircle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

// Status configuration for all possible states
const statusConfig = {
  not_connected: {
    icon: XCircle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-50',
    label: 'Not Connected',
  },
  pending: {
    icon: AlertCircle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    label: 'Pending',
  },
  connected: {
    icon: CheckCircle2,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Connected',
  },
};

// Integration status getters - each integration has its own logic
const getIntegrationStatus = {
  calendly: (calendly) => {
    if (!calendly) {
      return {
        status: 'not_connected',
        actionDescription: null,
      };
    }

    const isActive = calendly.active === true;
    const hasEventType = calendly.current_event_type_uri;

    // Pending: isActive is true but hasEventType is null/falsy
    if (isActive && !hasEventType) {
      return {
        status: 'pending',
        actionDescription: 'Please select an event type to complete the setup.',
      };
    }

    // Connected: isActive is true and hasEventType exists
    if (isActive && hasEventType) {
      return {
        status: 'connected',
        actionDescription: null,
      };
    }

    // Not connected: isActive is false
    return {
      status: 'not_connected',
      actionDescription: null,
    };
  },
  ghl: (ghl) => {
    if (!ghl) {
      return {
        status: 'not_connected',
        actionDescription: null,
      };
    }

    const isActive = ghl.active === true;
    const hasCalendarId = ghl.current_calendar_id;

    // Pending: isActive is true but hasCalendarId is null/falsy
    if (isActive && !hasCalendarId) {
      return {
        status: 'pending',
        actionDescription: 'Please select a calendar to complete the setup.',
      };
    }

    // Connected: isActive is true and hasCalendarId exists
    if (isActive && hasCalendarId) {
      return {
        status: 'connected',
        actionDescription: null,
      };
    }

    // Not connected: isActive is false
    return {
      status: 'not_connected',
      actionDescription: null,
    };
  },
  oncehub: (oncehub) => {
    if (!oncehub) {
      return {
        status: 'not_connected',
        actionDescription: null,
      };
    }

    const isActive = oncehub.active === true;

    if (isActive) {
      return {
        status: 'connected',
        actionDescription: null,
      };
    }

    return {
      status: 'not_connected',
      actionDescription: null,
    };
  },
  elevenlabs: (elevenlabs) => {
    if (!elevenlabs) {
      return {
        status: 'not_connected',
        actionDescription: null,
      };
    }

    const hasApiKey = elevenlabs.has_api_key === true;
    const isActive = elevenlabs.active === true;

    // Connected: has_api_key is true and active is true
    if (hasApiKey && isActive) {
      return {
        status: 'connected',
        actionDescription: null,
      };
    }

    // Not connected: otherwise
    return {
      status: 'not_connected',
      actionDescription: null,
    };
  },
};

// Integration configuration
const calendarIntegrations = [
  {
    key: 'calendly',
    name: 'Calendly',
    description: 'Connect your Calendly account to schedule meetings',
    icon: calendlyLogo,
    route: '/settings/integrations/calendly',
  },
  {
    key: 'ghl',
    name: 'GoHighLevel',
    description: 'Connect your GoHighLevel account to schedule meetings',
    icon: ghlLogo,
    route: '/settings/integrations/ghl',
  },
  {
    key: 'oncehub',
    name: 'OnceHub (ScheduleOnce)',
    description:
      'Connect your OnceHub / ScheduleOnce account to schedule meetings',
    icon: oncehubLogo,
    route: '/settings/integrations/oncehub',
  },
];

const voiceIntegrations = [
  {
    key: 'elevenlabs',
    name: 'ElevenLabs',
    description: 'Configure ElevenLabs API for text-to-speech',
    icon: elevenlabsLogo,
    route: '/settings/integrations/elevenlabs',
  },
];

const IntegrationsList = () => {
  const navigate = useNavigate();
  const { calendly, ghl, oncehub, elevenlabs, loading } = useIntegrationsStore(
    useShallow((state) => ({
      calendly: state.calendly,
      ghl: state.ghl,
      oncehub: state.oncehub,
      elevenlabs: state.elevenlabs,
      loading: state.loading,
    }))
  );

  const handleCardClick = (route) => {
    navigate(route);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-24'>
        <Loader2 className='mr-2 h-5 w-5 animate-spin text-muted-foreground' />
        <span className='text-sm text-muted-foreground'>
          Loading integrations...
        </span>
      </div>
    );
  }

  // Helper function to get integration data from store
  const getIntegrationData = (key) => {
    switch (key) {
      case 'calendly':
        return calendly;
      case 'ghl':
        return ghl;
      case 'oncehub':
        return oncehub;
      case 'elevenlabs':
        return elevenlabs;
      default:
        return null;
    }
  };

  // Helper function to build integration list with statuses
  const buildIntegrationsList = (configs) => {
    return configs.map((config) => {
      const integrationData = getIntegrationData(config.key);

      // Get status for this integration
      const statusGetter = getIntegrationStatus[config.key];
      const statusResult = statusGetter
        ? statusGetter(integrationData)
        : { status: 'not_connected', actionDescription: null };

      // Get status config for styling
      const status = statusConfig[statusResult.status];

      return {
        ...config,
        status: statusResult.status,
        statusConfig: status,
        actionDescription: statusResult.actionDescription,
      };
    });
  };

  // Helper function to render integration card
  const renderIntegrationCard = (integration) => {
    const StatusIcon = integration.statusConfig.icon;

    return (
      <Card
        key={integration.key}
        className='cursor-pointer transition-shadow hover:shadow-md'
        onClick={() => handleCardClick(integration.route)}
      >
        <CardHeader>
          <div className='flex items-start justify-between'>
            <div className='flex items-center gap-3'>
              <div className='rounded-lg bg-gray-100 p-2'>
                <img
                  src={integration.icon}
                  alt={integration.name}
                  className='h-6 w-6 object-contain'
                />
              </div>
              <div>
                <CardTitle className='text-base'>{integration.name}</CardTitle>
              </div>
            </div>
            <div
              className={`flex items-center gap-1 rounded-full px-2 py-1 ${integration.statusConfig.bgColor}`}
            >
              <StatusIcon
                className={`h-4 w-4 ${integration.statusConfig.color}`}
              />
              <span
                className={`text-xs font-medium ${integration.statusConfig.color}`}
              >
                {integration.statusConfig.label}
              </span>
            </div>
          </div>
          <CardDescription className='mt-2'>
            {integration.description}
          </CardDescription>
        </CardHeader>
        {integration.status === 'pending' && integration.actionDescription && (
          <CardContent>
            <div className='rounded-md bg-yellow-50 p-3'>
              <p className='text-sm text-yellow-800'>
                {integration.actionDescription}
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  const calendarIntegrationsList = buildIntegrationsList(calendarIntegrations);
  const voiceIntegrationsList = buildIntegrationsList(voiceIntegrations);

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>Integrations</h3>
          <p className='text-sm text-muted-foreground'>
            Connect and manage your third-party integrations
          </p>
        </div>
      </div>
      <Separator />

      {/* Calendar Integrations Section */}
      <div className='space-y-4'>
        <div>
          <h4 className='text-base font-semibold'>Calendar</h4>
          <p className='text-sm text-muted-foreground'>
            Connect calendar integrations to schedule meetings
          </p>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
          {calendarIntegrationsList.map(renderIntegrationCard)}
        </div>
      </div>

      {/* Voice Integrations Section */}
      <div className='space-y-4'>
        <div>
          <h4 className='text-base font-semibold'>Voice</h4>
          <p className='text-sm text-muted-foreground'>
            Configure voice integrations for text-to-speech
          </p>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
          {voiceIntegrationsList.map(renderIntegrationCard)}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsList;
